import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { fixPhotoUrl } from '@/lib/photoUrl';
import { Badge } from '@/components/ui/badge';
import {
  MapPin, Bed, Bath, Users, Wifi, ChevronLeft, ChevronRight,
  Star, Check, Home, Wind, Waves, Dumbbell, Car, Tv, UtensilsCrossed,
  Flame, TreePine, Eye, Edit, ExternalLink, X
} from 'lucide-react';

/* ── Amenity icon map ─────────────────────────────────────── */
const AMENITY_ICONS = {
  'WiFi': Wifi, 'Air Conditioning': Wind, 'Private Pool': Waves,
  'Heated Pool': Waves, 'Gym': Dumbbell, 'Parking': Car,
  'TV': Tv, 'Kitchen': UtensilsCrossed, 'Fireplace': Flame,
  'Garden': TreePine, 'Balcony': Home, 'Sea View': Eye,
  'Ocean View': Eye, 'Mountain View': TreePine,
};

const amenityIcon = (name) => {
  const Icon = AMENITY_ICONS[name] || Check;
  return <Icon className="w-4 h-4" />;
};

/* ── Photo gallery ────────────────────────────────────────── */
const PhotoGallery = ({ photos, title }) => {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  if (!photos?.length) {
    return (
      <div className="w-full h-72 md:h-[420px] rounded-2xl bg-gradient-to-br from-[#194a50]/20 to-[#d8a28c]/20 flex items-center justify-center">
        <Home className="w-20 h-20 text-[#194a50]/25" />
      </div>
    );
  }

  const prev = () => setCurrent((c) => (c - 1 + photos.length) % photos.length);
  const next = () => setCurrent((c) => (c + 1) % photos.length);

  return (
    <>
      {/* Main hero image + thumbs */}
      <div className="grid grid-cols-4 grid-rows-2 gap-2 h-72 md:h-[420px] rounded-2xl overflow-hidden">
        {/* Main large image */}
        <div
          className="col-span-4 md:col-span-3 row-span-2 relative cursor-pointer group"
          onClick={() => setLightbox(current)}
        >
          <img
            src={fixPhotoUrl(photos[current])}
            alt={`${title} — ${current + 1}`}
            className="w-full h-full object-cover group-hover:brightness-95 transition"
          />
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
            {current + 1} / {photos.length}
          </div>
        </div>

        {/* Side thumbnails — desktop only */}
        {photos.slice(1, 3).map((photo, i) => (
          <div
            key={i}
            className="hidden md:block relative cursor-pointer overflow-hidden"
            onClick={() => { setCurrent(i + 1); setLightbox(i + 1); }}
          >
            <img
              src={fixPhotoUrl(photo)}
              alt={`${title} — ${i + 2}`}
              className="w-full h-full object-cover hover:brightness-95 transition"
            />
            {i === 1 && photos.length > 3 && (
              <div
                className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer"
                onClick={(e) => { e.stopPropagation(); setLightbox(0); }}
              >
                <span className="text-white font-semibold text-sm">+{photos.length - 2} more</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
          {photos.map((photo, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`shrink-0 w-14 h-10 rounded-lg overflow-hidden border-2 transition ${
                i === current ? 'border-[#194a50]' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={fixPhotoUrl(photo)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            onClick={() => setLightbox(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
            onClick={(e) => { e.stopPropagation(); setLightbox((l) => (l - 1 + photos.length) % photos.length); }}
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <img
            src={fixPhotoUrl(photos[lightbox])}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
            onClick={(e) => { e.stopPropagation(); setLightbox((l) => (l + 1) % photos.length); }}
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {lightbox + 1} / {photos.length}
          </div>
        </div>
      )}
    </>
  );
};

/* ── Stat pill ────────────────────────────────────────────── */
const Stat = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-[#194a50]">
    <Icon className="w-5 h-5 text-[#194a50]/60" />
    <span className="text-sm font-medium">{label}</span>
  </div>
);

/* ── Main page ────────────────────────────────────────────── */
const PropertyPreviewPage = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await axios.get(`${API}/properties/${id}`);
        setProperty(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0c2431 0%, #194a50 100%)' }}>
        <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !property) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4"
        style={{ background: 'linear-gradient(135deg, #0c2431 0%, #194a50 100%)' }}>
        <Home className="w-16 h-16 text-white/30" />
        <p className="text-white text-xl font-semibold">Property not found</p>
        <button onClick={() => window.close()} className="text-white/60 hover:text-white text-sm underline">
          Close tab
        </button>
      </div>
    );
  }

  const visibleAmenities = property.amenities?.filter(Boolean) || [];

  return (
    <div className="min-h-screen bg-[#f7f8f9]">

      {/* ── Admin preview banner ────────────────────────────── */}
      <div
        className="sticky top-0 z-50 flex items-center justify-between px-4 py-2.5 text-white text-sm"
        style={{ background: 'linear-gradient(90deg, #0c2431, #194a50)' }}
      >
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 opacity-70" />
          <span className="font-medium">Admin Preview</span>
          <span className="opacity-50">·</span>
          <span className="opacity-60">This is how the property appears to customers</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open(`/properties/${id}`, '_blank')}
            className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-1 rounded-full text-xs font-medium transition"
          >
            <Edit className="w-3.5 h-3.5" />
            Edit in Admin
          </button>
          <button
            onClick={() => window.close()}
            className="flex items-center gap-1.5 text-white/60 hover:text-white px-2 py-1 rounded-full text-xs transition"
          >
            <X className="w-3.5 h-3.5" />
            Close
          </button>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

        {/* Title row */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <h1 className="text-3xl font-bold text-[#0c2431]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {property.title}
              </h1>
              {property.reference && (
                <span className="text-xs bg-[#e1dad2] text-[#194a50] px-2.5 py-1 rounded-full font-mono font-medium">
                  {property.reference}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[#194a50]/70">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">
                {[property.location?.address_line_1, property.location?.city, property.location?.country]
                  .filter(Boolean).join(', ')}
              </span>
            </div>
          </div>

          {/* Price */}
          <div className="shrink-0 text-right">
            <div className="text-3xl font-bold text-[#0c2431]">
              €{property.base_nightly_rate}
              <span className="text-base font-normal text-[#194a50]/60 ml-1">/night</span>
            </div>
            {property.min_stay_nights && (
              <p className="text-xs text-[#194a50]/50 mt-0.5">Min. {property.min_stay_nights} nights</p>
            )}
          </div>
        </div>

        {/* Gallery */}
        <PhotoGallery photos={property.photos} title={property.title} />

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left: details */}
          <div className="lg:col-span-2 space-y-8">

            {/* Stats bar */}
            <div className="flex flex-wrap gap-6 py-5 px-6 bg-white rounded-2xl border border-[#194a50]/8 shadow-sm">
              <Stat icon={Bed}   label={`${property.bedrooms  ?? '—'} bedroom${property.bedrooms  !== 1 ? 's' : ''}`} />
              <Stat icon={Bath}  label={`${property.bathrooms ?? '—'} bathroom${property.bathrooms !== 1 ? 's' : ''}`} />
              <Stat icon={Users} label={`Up to ${property.max_guests ?? '—'} guests`} />
            </div>

            {/* Description */}
            {property.description && (
              <div className="bg-white rounded-2xl border border-[#194a50]/8 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-[#0c2431] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  About this property
                </h2>
                <p className="text-[#194a50]/80 leading-relaxed whitespace-pre-line text-sm">
                  {property.description}
                </p>
              </div>
            )}

            {/* Amenities */}
            {visibleAmenities.length > 0 && (
              <div className="bg-white rounded-2xl border border-[#194a50]/8 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-[#0c2431] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Amenities
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {visibleAmenities.map((amenity) => (
                    <div key={amenity} className="flex items-center gap-2.5 text-[#194a50] bg-[#f4f7f8] rounded-xl px-3 py-2.5">
                      {amenityIcon(amenity)}
                      <span className="text-sm font-medium">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location */}
            {(property.location?.city || property.location?.country) && (
              <div className="bg-white rounded-2xl border border-[#194a50]/8 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-[#0c2431] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Location
                </h2>
                <div className="flex items-start gap-2 text-[#194a50]/70">
                  <MapPin className="w-5 h-5 mt-0.5 shrink-0 text-[#d8a28c]" />
                  <div>
                    {property.location?.address_line_1 && (
                      <p className="text-sm font-medium text-[#0c2431]">{property.location.address_line_1}</p>
                    )}
                    <p className="text-sm">
                      {[property.location?.city, property.location?.postal_code, property.location?.country]
                        .filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: booking summary card */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 bg-white rounded-2xl border border-[#194a50]/10 shadow-lg p-6 space-y-5">
              <div>
                <div className="text-2xl font-bold text-[#0c2431]">
                  €{property.base_nightly_rate}
                  <span className="text-sm font-normal text-[#194a50]/55 ml-1">/night</span>
                </div>
                {property.min_stay_nights && (
                  <p className="text-xs text-[#194a50]/50 mt-0.5">
                    Minimum stay: {property.min_stay_nights} nights
                  </p>
                )}
              </div>

              <div className="space-y-2 text-sm text-[#194a50]/70">
                {property.bedrooms    && <div className="flex justify-between"><span>Bedrooms</span><span className="font-medium text-[#0c2431]">{property.bedrooms}</span></div>}
                {property.bathrooms   && <div className="flex justify-between"><span>Bathrooms</span><span className="font-medium text-[#0c2431]">{property.bathrooms}</span></div>}
                {property.max_guests  && <div className="flex justify-between"><span>Max guests</span><span className="font-medium text-[#0c2431]">{property.max_guests}</span></div>}
                {property.cleaning_fee > 0 && (
                  <div className="flex justify-between">
                    <span>Cleaning fee</span>
                    <span className="font-medium text-[#0c2431]">€{property.cleaning_fee}</span>
                  </div>
                )}
              </div>

              <div className="border-t border-[#194a50]/8 pt-4">
                <button
                  className="w-full py-3 rounded-full text-white font-semibold text-sm transition hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #194a50, #10555e)' }}
                  onClick={() => alert('Booking is handled through the customer portal.')}
                >
                  Check Availability
                </button>
                <p className="text-[10px] text-center text-[#194a50]/40 mt-2">
                  Admin preview — bookings via customer portal
                </p>
              </div>

              {/* Admin quick info */}
              {property.key_reference && (
                <div className="border-t border-[#194a50]/8 pt-3">
                  <p className="text-xs text-[#194a50]/50 font-medium mb-1">Admin Info</p>
                  <div className="text-xs text-[#194a50]/70 space-y-1">
                    <div className="flex justify-between">
                      <span>Key ref</span>
                      <span className="font-mono font-medium text-[#0c2431]">{property.key_reference}</span>
                    </div>
                    {property.is_published !== undefined && (
                      <div className="flex justify-between">
                        <span>Published</span>
                        <span className={`font-medium ${property.is_published ? 'text-green-600' : 'text-red-500'}`}>
                          {property.is_published ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="mt-16 py-8 border-t border-[#194a50]/10 text-center text-xs text-[#194a50]/40">
        OceanHome · Admin Property Preview
      </footer>
    </div>
  );
};

export default PropertyPreviewPage;
